/**
 * Single simulation instance
 * Owns a WASM module and renderer, manages lifecycle
 */

import type { Scenario } from '../types/scenario';
import type {
  SimulationState,
  SimulationStatus,
} from '../types/simulation';
import type { IRenderer } from '../types/renderer';
import { WasmModule } from './WasmModule';
import { ThreeRenderer } from '../rendering/ThreeRenderer';

export interface SimulationEvents {
  onStateChange?: (state: SimulationState) => void;
  onError?: (error: Error) => void;
}

export class Simulation {
  readonly id: string;
  private wasm: WasmModule | null = null;
  private renderer: IRenderer | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private _status: SimulationStatus = 'empty';
  private _scenarioName: string | null = null;
  private animationFrame: number = 0;
  private lastFrameTime: number = 0;
  private frameCount: number = 0;
  private fps: number = 0;
  private events: SimulationEvents;

  constructor(id: string, events: SimulationEvents = {}) {
    this.id = id;
    this.events = events;
  }

  get status(): SimulationStatus {
    return this._status;
  }

  get scenarioName(): string | null {
    return this._scenarioName;
  }

  get isRunning(): boolean {
    return this._status === 'running';
  }

  get isPaused(): boolean {
    return this._status === 'paused';
  }

  get isReady(): boolean {
    return this._status === 'ready' || this._status === 'running' || this._status === 'paused';
  }

  /**
   * Initialize the simulation with a canvas element
   */
  async initialize(canvas: HTMLCanvasElement): Promise<void> {
    if (this._status !== 'empty') {
      throw new Error('Simulation already initialized');
    }

    this.canvas = canvas;
    this.setStatus('loading');

    try {
      // Load WASM module
      this.wasm = await WasmModule.load();

      // Initialize renderer
      this.renderer = new ThreeRenderer();
      await this.renderer.initialize(canvas);

      // Set default world bounds
      this.wasm.api.setWorldBounds(-200, -200, -200, 200, 200, 200, 10);

      this.setStatus('ready');
    } catch (error) {
      this.setStatus('error');
      this.events.onError?.(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }

  /**
   * Load a scenario into this simulation
   */
  async loadScenario(scenario: Scenario): Promise<void> {
    if (!this.wasm) {
      throw new Error('Simulation not initialized');
    }

    const api = this.wasm.api;
    const wasPaused = this._status === 'paused';

    // Pause while loading
    if (this._status === 'running') {
      this.pause();
    }

    try {
      // Clear existing entities (destroy all)
      const existingCount = api.getEntityCount();
      for (let i = existingCount - 1; i >= 0; i--) {
        api.destroyEntity(i);
      }

      // Configure world
      const { bounds, cellSize, temperature, dt } = scenario.world;
      api.setWorldBounds(
        bounds.min[0],
        bounds.min[1],
        bounds.min[2],
        bounds.max[0],
        bounds.max[1],
        bounds.max[2],
        cellSize
      );
      api.setTemperature(temperature);
      api.setDt(dt);

      // Spawn entities
      for (const group of scenario.entities) {
        const { archetype, count, randomSpawn, position } = group;

        if (randomSpawn) {
          // Use batch spawn for random positions
          api.spawnRandom(
            count,
            archetype.id,
            archetype.mass,
            archetype.radius,
            archetype.charge,
            archetype.hydrophobicity,
            archetype.diffusionCoeff,
            randomSpawn.min,
            randomSpawn.max
          );
        } else if (position) {
          // Spawn at specific position
          for (let i = 0; i < count; i++) {
            const e = api.createEntity();
            api.setPosition(e, position[0], position[1], position[2]);
            api.setVelocity(e, 0, 0, 0);
            api.setPhysicalProps(
              e,
              archetype.mass,
              archetype.radius,
              archetype.charge,
              archetype.hydrophobicity,
              archetype.diffusionCoeff,
              0.5 // default elasticity
            );
            api.setMoleculeType(e, archetype.id, 0);
            api.setLOD(e, 0);
            api.setTags(e, 1); // TAG_ACTIVE
          }
        }
      }

      this._scenarioName = scenario.name;

      // Restore previous running state or set to ready
      if (!wasPaused && this._status === 'paused') {
        this.play();
      } else {
        this.setStatus('ready');
      }
    } catch (error) {
      this.setStatus('error');
      this.events.onError?.(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }

  /**
   * Start/resume simulation
   */
  play(): void {
    if (!this.wasm || !this.renderer) {
      return;
    }

    if (this._status === 'running') {
      return;
    }

    this.wasm.api.resume();
    this.setStatus('running');
    this.startRenderLoop();
  }

  /**
   * Pause simulation
   */
  pause(): void {
    if (!this.wasm) {
      return;
    }

    this.wasm.api.pause();
    this.setStatus('paused');
    this.stopRenderLoop();
  }

  /**
   * Step one tick (while paused)
   */
  step(): void {
    if (!this.wasm || !this.renderer) {
      return;
    }

    this.wasm.api.step();
    this.renderFrame();
  }

  /**
   * Get current simulation state
   */
  getState(): SimulationState {
    const entityCount = this.wasm?.api.getEntityCount() ?? 0;
    const tick = this.wasm?.api.getTick() ?? 0;
    const temperature = this.wasm?.api.getTemperature() ?? 0;
    const dt = this.wasm?.api.getDt() ?? 0;

    return {
      id: this.id,
      status: this._status,
      tick,
      entityCount,
      fps: this.fps,
      scenarioName: this._scenarioName,
      temperature,
      dt,
    };
  }

  /**
   * Clean up all resources
   */
  dispose(): void {
    this.stopRenderLoop();
    this.renderer?.dispose();
    this.wasm?.dispose();
    this.renderer = null;
    this.wasm = null;
    this.canvas = null;
    this._status = 'empty';
    this._scenarioName = null;
  }

  /**
   * Handle canvas resize
   */
  resize(width: number, height: number): void {
    this.renderer?.resize(width, height);
  }

  private setStatus(status: SimulationStatus): void {
    this._status = status;
    this.events.onStateChange?.(this.getState());
  }

  private startRenderLoop(): void {
    if (this.animationFrame) {
      return;
    }

    this.lastFrameTime = performance.now();
    this.frameCount = 0;

    const loop = (): void => {
      if (this._status !== 'running') {
        this.animationFrame = 0;
        return;
      }

      // Step simulation
      this.wasm?.api.step();

      // Render
      this.renderFrame();

      // Calculate FPS
      this.frameCount++;
      const now = performance.now();
      const elapsed = now - this.lastFrameTime;
      if (elapsed >= 1000) {
        this.fps = Math.round((this.frameCount * 1000) / elapsed);
        this.frameCount = 0;
        this.lastFrameTime = now;
      }

      this.animationFrame = requestAnimationFrame(loop);
    };

    this.animationFrame = requestAnimationFrame(loop);
  }

  private stopRenderLoop(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = 0;
    }
  }

  private renderFrame(): void {
    if (!this.wasm || !this.renderer) {
      return;
    }

    const { positions, count } = this.wasm.getPositionsView();
    this.renderer.render(positions, count);
  }
}
